import { c as _c } from "react/compiler-runtime";
import { NodeIcon } from "@/components/ui/node-icon";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { getExecutionStatusClasses, getStatusLabel } from "@/hooks/useNodeExecutionStatus";
import { cn } from "@/lib/utils";
import type { NodeExecutionStatus } from "@/types/runStream";
import { Handle, Position } from "@xyflow/react";
import { IconCheckOutline18, IconMinusOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
import React, { type ReactNode } from "react";
export type TriggerNodeShellStatus = "idle" | NodeExecutionStatus;
export interface TriggerNodeShellProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onMouseEnter" | "onMouseLeave"> {
  /** React Flow node id — used as data attribute for live cursor targeting and a11y. */
  id?: string;
  /** Display name shown in bold next to the icon. */
  name: string;
  /** Small line beneath the name (e.g. "Trigger", "Trigger output · 1.2s"). */
  subtitle?: ReactNode;
  /** Logo / icon URL for the node — passed through to {@link NodeIcon}. */
  iconUrl?: string;
  /** Optional connector overlay shown on top-right of the icon. */
  connectorIconUrl?: string;
  /** Connector type label ({@link NodeIcon} uses it when no `connectorIconUrl` is set). */
  connectorType?: string;
  /** Execution status — drives badge, deboss, and tinted background. */
  status?: TriggerNodeShellStatus;
  /** Selected (drives blue border + shimmer ring). */
  selected?: boolean;
  /** Validation error (red dashed treatment). */
  hasValidationErrors?: boolean;
  /** Streaming-active highlight (AI is currently building this node). */
  isStreamingActive?: boolean;
  /** AI-update highlight (the node was just updated by AI). */
  showAIHighlight?: boolean;
  /** Shimmer ring shown for selection (when no other border treatment claims priority). */
  showRotatingBorder?: boolean;
  /** Same shimmer ring used on hover (without selection). */
  showHoverBorder?: boolean;
  /** Streaming entrance / edit highlight class applied to the outer wrapper. */
  streamingClass?: string;
  /** Hide the source handle — useful for terminal nodes / template view. */
  hideSourceHandle?: boolean;
  /** When true, lays the source handle on the right (instead of bottom). */
  horizontalHandles?: boolean;
  /** Compact mode hides the subtitle row, banners, and body. */
  compact?: boolean;
  /** Mouse handlers — owned by the wrapper because they may also update global hover state. */
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** Optional ref forwarded to the outer wrapper (used by the wrapper to anchor toolbars). */
  nodeRef?: React.Ref<HTMLDivElement>;
  /** Toolbar slot — description / validation toolbars rendered above the chrome. */
  toolbarSlot?: ReactNode;
  /** Banner slot — Schedule countdown, "Trigger Now" / Run buttons, widgets. */
  bannerSlot?: ReactNode;
  /** Body slot — settings preview, output preview, custom node content. */
  bodySlot?: ReactNode;
  /** Execution details slot — input/output details panel after a finished run. */
  executionDetailsSlot?: ReactNode;
  /** Comments badge slot — small overlay shown when the node has comments. */
  commentsBadgeSlot?: ReactNode;
  /** Selection indicator slot — collaborative editing presence indicator. */
  selectionIndicatorSlot?: ReactNode;
  /** Optional className for the outer wrapper (used for `data-api-node-id`-driven selectors). */
  className?: string;
  /** Optional data attribute used by selectors that target the API node id. */
  dataApiNodeId?: string;
  /** Optional click handler for the failure status badge (opens error detail modal). */
  onErrorBadgeClick?: () => void;
  /** Color to pass to {@link NodeIcon} (defaults to trigger blue). */
  iconColor?: string;
  /** NodeIcon caching id (typically the trigger `type`). Falls back to {@link id} or `name`. */
  iconId?: string;
  /** NodeIcon subject (display name used for AI-generated icon prompts). Falls back to {@link name}. */
  iconSubject?: string;
  /** NodeIcon context (description used for AI-generated icon prompts). */
  iconContext?: string;
  /** NodeIcon alt text. Falls back to {@link name}. */
  iconAlt?: string;
}

/**
 * Status badge bubble (top-left). Mirrors the inline implementation that lived
 * inside `TriggerNode.tsx` — kept locally because triggers do not show the
 * `auto_swapping` state and clickable failure badge is a trigger-only affordance.
 */
function TriggerStatusBadge(t0) {
  const $ = _c(24);
  const {
    executionStatus,
    onErrorBadgeClick
  } = t0;
  const t1 = `Node status: ${getStatusLabel(executionStatus)}`;
  let t2;
  if ($[0] !== executionStatus || $[1] !== onErrorBadgeClick) {
    t2 = executionStatus === "failure" && onErrorBadgeClick ? e => {
      e.stopPropagation();
      onErrorBadgeClick();
    } : undefined;
    $[0] = executionStatus;
    $[1] = onErrorBadgeClick;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = executionStatus === "running" && "border-blue-500 bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400";
  const t4 = executionStatus === "success" && "border-green-500 bg-green-100 text-green-600 dark:bg-green-900/50 dark:text-green-400";
  const t5 = executionStatus === "failure" && "cursor-pointer border-red-400 bg-red-100 text-red-600 hover:bg-red-200 dark:border-red-500 dark:bg-red-900/50 dark:text-red-400 dark:hover:bg-red-900/70";
  const t6 = executionStatus === "skipped" && "bg-ds-neutral-100 text-ds-text-subtle dark:bg-ds-neutral-800 dark:text-ds-text-disabled border-ds-border-default";
  let t7;
  if ($[3] !== t3 || $[4] !== t4 || $[5] !== t5 || $[6] !== t6) {
    t7 = cn("shadow-ds-soft absolute -left-3 -top-3 z-20 flex h-6 w-6 items-center justify-center rounded-full border-2 backdrop-blur-md", t3, t4, t5, t6);
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== executionStatus) {
    t8 = executionStatus === "running" && <PixelLoader size="xs" aria-label="Running" />;
    $[8] = executionStatus;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== executionStatus) {
    t9 = executionStatus === "success" && <IconCheckOutline18 className="h-3 w-3" aria-hidden="true" />;
    $[10] = executionStatus;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== executionStatus) {
    t10 = executionStatus === "failure" && <IconXmarkOutline18 className="h-3 w-3" aria-hidden="true" />;
    $[12] = executionStatus;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== executionStatus) {
    t11 = executionStatus === "skipped" && <IconMinusOutline18 className="h-3 w-3" aria-hidden="true" />;
    $[14] = executionStatus;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t1 || $[17] !== t10 || $[18] !== t11 || $[19] !== t2 || $[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t12 = <div role="status" aria-label={t1} onClick={t2} className={t7}>{t8}{t9}{t10}{t11}</div>;
    $[16] = t1;
    $[17] = t10;
    $[18] = t11;
    $[19] = t2;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
}

/**
 * Pure presentational chrome for TriggerNode.
 *
 * Owns the outer card surface, status colouring, source handle, and slots for body,
 * banners, toolbars, comments, and the selection indicator. Calls **no hooks**
 * besides whatever `<Handle>` does internally — that is React Flow's
 * `useNodeId` / `useStore` and is provider-only (no fetch / subscription).
 *
 * Stories / Ladle wrap usages in `<ReactFlowProvider>` so the handles render.
 */
export function TriggerNodeShell(t0) {
  const $ = _c(113);
  let bannerSlot;
  let bodySlot;
  let className;
  let commentsBadgeSlot;
  let connectorIconUrl;
  let connectorType;
  let dataApiNodeId;
  let executionDetailsSlot;
  let iconAlt;
  let iconColor;
  let iconContext;
  let iconId;
  let iconSubject;
  let iconUrl;
  let id;
  let name;
  let nodeRef;
  let onErrorBadgeClick;
  let onMouseEnter;
  let onMouseLeave;
  let rest;
  let selectionIndicatorSlot;
  let streamingClass;
  let subtitle;
  let t1;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let toolbarSlot;
  if ($[0] !== t0) {
    ({
      id,
      name,
      subtitle,
      iconUrl,
      connectorIconUrl,
      connectorType,
      status: t1,
      selected: t2,
      hasValidationErrors: t3,
      isStreamingActive: t4,
      showAIHighlight: t5,
      showRotatingBorder: t6,
      showHoverBorder: t7,
      streamingClass,
      hideSourceHandle: t8,
      horizontalHandles: t9,
      compact: t10,
      onMouseEnter,
      onMouseLeave,
      nodeRef,
      toolbarSlot,
      bannerSlot,
      bodySlot,
      executionDetailsSlot,
      commentsBadgeSlot,
      selectionIndicatorSlot,
      className,
      dataApiNodeId,
      onErrorBadgeClick,
      iconColor,
      iconId,
      iconSubject,
      iconContext,
      iconAlt,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = bannerSlot;
    $[2] = bodySlot;
    $[3] = className;
    $[4] = commentsBadgeSlot;
    $[5] = connectorIconUrl;
    $[6] = connectorType;
    $[7] = dataApiNodeId;
    $[8] = executionDetailsSlot;
    $[9] = iconAlt;
    $[10] = iconColor;
    $[11] = iconContext;
    $[12] = iconId;
    $[13] = iconSubject;
    $[14] = iconUrl;
    $[15] = id;
    $[16] = name;
    $[17] = nodeRef;
    $[18] = onErrorBadgeClick;
    $[19] = onMouseEnter;
    $[20] = onMouseLeave;
    $[21] = rest;
    $[22] = selectionIndicatorSlot;
    $[23] = streamingClass;
    $[24] = subtitle;
    $[25] = t1;
    $[26] = t10;
    $[27] = t2;
    $[28] = t3;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
    $[35] = toolbarSlot;
  } else {
    bannerSlot = $[1];
    bodySlot = $[2];
    className = $[3];
    commentsBadgeSlot = $[4];
    connectorIconUrl = $[5];
    connectorType = $[6];
    dataApiNodeId = $[7];
    executionDetailsSlot = $[8];
    iconAlt = $[9];
    iconColor = $[10];
    iconContext = $[11];
    iconId = $[12];
    iconSubject = $[13];
    iconUrl = $[14];
    id = $[15];
    name = $[16];
    nodeRef = $[17];
    onErrorBadgeClick = $[18];
    onMouseEnter = $[19];
    onMouseLeave = $[20];
    rest = $[21];
    selectionIndicatorSlot = $[22];
    streamingClass = $[23];
    subtitle = $[24];
    t1 = $[25];
    t10 = $[26];
    t2 = $[27];
    t3 = $[28];
    t4 = $[29];
    t5 = $[30];
    t6 = $[31];
    t7 = $[32];
    t8 = $[33];
    t9 = $[34];
    toolbarSlot = $[35];
  }
  const status = t1 === undefined ? "idle" : t1;
  const selected = t2 === undefined ? false : t2;
  const hasValidationErrors = t3 === undefined ? false : t3;
  const isStreamingActive = t4 === undefined ? false : t4;
  const showAIHighlight = t5 === undefined ? false : t5;
  const showRotatingBorder = t6 === undefined ? false : t6;
  const showHoverBorder = t7 === undefined ? false : t7;
  const hideSourceHandle = t8 === undefined ? false : t8;
  const horizontalHandles = t9 === undefined ? false : t9;
  const compact = t10 === undefined ? false : t10;
  const hasActiveRun = status !== "idle";
  let connector;
  let resolvedIconColor;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  if ($[36] !== className || $[37] !== connectorIconUrl || $[38] !== connectorType || $[39] !== dataApiNodeId || $[40] !== hasActiveRun || $[41] !== hasValidationErrors || $[42] !== iconColor || $[43] !== id || $[44] !== isStreamingActive || $[45] !== nodeRef || $[46] !== onMouseEnter || $[47] !== onMouseLeave || $[48] !== rest || $[49] !== selected || $[50] !== showAIHighlight || $[51] !== status || $[52] !== streamingClass) {
    const statusClasses = hasActiveRun ? getExecutionStatusClasses(status as NodeExecutionStatus) : "";
    let t17;
    if ($[61] !== connectorIconUrl || $[62] !== connectorType) {
      t17 = connectorIconUrl ? {
        type: connectorType ?? "connector",
        logoUrl: connectorIconUrl
      } : connectorType ? {
        type: connectorType
      } : undefined;
      $[61] = connectorIconUrl;
      $[62] = connectorType;
      $[63] = t17;
    } else {
      t17 = $[63];
    }
    connector = t17;
    resolvedIconColor = iconColor ?? "#2450FF";
    t11 = rest;
    t12 = nodeRef;
    t13 = dataApiNodeId ?? id;
    t14 = onMouseEnter;
    t15 = onMouseLeave;
    t16 = cn("border-ds-neutral-200 bg-ds-surface-secondary rounded-ds-14 group relative w-[280px] cursor-pointer overflow-visible border p-1.5 transition-[border-color,box-shadow,opacity]", streamingClass, hasActiveRun ? "node-deboss" : "node-emboss", showAIHighlight && "animate-ai-highlight bg-ds-surface-card shadow-ds-elev-2 border-dashed border-blue-500 shadow-blue-500/30 ring-2 ring-blue-500/50", !showAIHighlight && hasActiveRun && statusClasses, !showAIHighlight && !hasActiveRun && hasValidationErrors && "border-dashed border-red-500 bg-red-50/50 shadow-red-500/10 dark:bg-red-950/20", isStreamingActive && "streaming-active-node bg-ds-surface-card shadow-ds-elev-2 border-2 border-blue-500 shadow-blue-500/20 dark:border-blue-400 dark:shadow-blue-400/20", !isStreamingActive && !showAIHighlight && !hasActiveRun && !hasValidationErrors && (selected ? "node-selected-blue bg-ds-surface-card" : "border-ds-neutral-300 dark:border-ds-neutral-700 bg-ds-surface-card"), !showAIHighlight && hasActiveRun && "bg-ds-surface-card", className);
    $[36] = className;
    $[37] = connectorIconUrl;
    $[38] = connectorType;
    $[39] = dataApiNodeId;
    $[40] = hasActiveRun;
    $[41] = hasValidationErrors;
    $[42] = iconColor;
    $[43] = id;
    $[44] = isStreamingActive;
    $[45] = nodeRef;
    $[46] = onMouseEnter;
    $[47] = onMouseLeave;
    $[48] = rest;
    $[49] = selected;
    $[50] = showAIHighlight;
    $[51] = status;
    $[52] = streamingClass;
    $[53] = connector;
    $[54] = resolvedIconColor;
    $[55] = t11;
    $[56] = t12;
    $[57] = t13;
    $[58] = t14;
    $[59] = t15;
    $[60] = t16;
  } else {
    connector = $[53];
    resolvedIconColor = $[54];
    t11 = $[55];
    t12 = $[56];
    t13 = $[57];
    t14 = $[58];
    t15 = $[59];
    t16 = $[60];
  }
  let t17;
  if ($[64] !== showHoverBorder || $[65] !== showRotatingBorder) {
    t17 = (showRotatingBorder || showHoverBorder) && <><div className="shimmer-border-blue rounded-ds-lg pointer-events-none absolute -inset-[3px] -z-10 overflow-hidden" /><div className="rounded-ds-14 bg-ds-surface-card pointer-events-none absolute -inset-[1px] -z-10" /></>;
    $[64] = showHoverBorder;
    $[65] = showRotatingBorder;
    $[66] = t17;
  } else {
    t17 = $[66];
  }
  let t18;
  if ($[67] !== hasActiveRun || $[68] !== onErrorBadgeClick || $[69] !== status) {
    t18 = hasActiveRun && <TriggerStatusBadge executionStatus={status as NodeExecutionStatus} onErrorBadgeClick={onErrorBadgeClick} />;
    $[67] = hasActiveRun;
    $[68] = onErrorBadgeClick;
    $[69] = status;
    $[70] = t18;
  } else {
    t18 = $[70];
  }
  const t19 = iconId || id || name || "trigger";
  const t20 = iconSubject || name;
  const t21 = iconContext || "";
  const t22 = compact ? "h-8 w-8" : "h-10 w-10";
  const t23 = iconAlt || name;
  let t24;
  if ($[71] !== connector || $[72] !== iconUrl || $[73] !== resolvedIconColor || $[74] !== t19 || $[75] !== t20 || $[76] !== t21 || $[77] !== t22 || $[78] !== t23) {
    t24 = <NodeIcon id={t19} subject={t20} context={t21} logoUrl={iconUrl} connector={connector} className={t22} alt={t23} color={resolvedIconColor} />;
    $[71] = connector;
    $[72] = iconUrl;
    $[73] = resolvedIconColor;
    $[74] = t19;
    $[75] = t20;
    $[76] = t21;
    $[77] = t22;
    $[78] = t23;
    $[79] = t24;
  } else {
    t24 = $[79];
  }
  let t25;
  if ($[80] !== name) {
    t25 = <div className="body-sm-medium truncate">{name}</div>;
    $[80] = name;
    $[81] = t25;
  } else {
    t25 = $[81];
  }
  const t26 = !compact && subtitle ? subtitle : null;
  let t27;
  if ($[82] !== t25 || $[83] !== t26) {
    t27 = <div className="min-w-0 flex-1">{t25}{t26}</div>;
    $[82] = t25;
    $[83] = t26;
    $[84] = t27;
  } else {
    t27 = $[84];
  }
  let t28;
  if ($[85] !== t24 || $[86] !== t27) {
    t28 = <div className="flex items-center gap-3">{t24}{t27}</div>;
    $[85] = t24;
    $[86] = t27;
    $[87] = t28;
  } else {
    t28 = $[87];
  }
  let t29;
  if ($[88] !== bannerSlot || $[89] !== bodySlot || $[90] !== compact || $[91] !== executionDetailsSlot) {
    t29 = !compact && <>{bannerSlot}{bodySlot}{executionDetailsSlot}</>;
    $[88] = bannerSlot;
    $[89] = bodySlot;
    $[90] = compact;
    $[91] = executionDetailsSlot;
    $[92] = t29;
  } else {
    t29 = $[92];
  }
  let t30;
  if ($[93] !== hideSourceHandle || $[94] !== horizontalHandles) {
    t30 = !hideSourceHandle && <Handle type="source" position={horizontalHandles ? Position.Right : Position.Bottom} className="!border-ds-neutral-300 dark:!border-ds-neutral-600 !bg-ds-surface-card !h-3 !w-3 !border-2" />;
    $[93] = hideSourceHandle;
    $[94] = horizontalHandles;
    $[95] = t30;
  } else {
    t30 = $[95];
  }
  let t31;
  if ($[96] !== commentsBadgeSlot || $[97] !== selectionIndicatorSlot || $[98] !== t18 || $[99] !== t28 || $[100] !== t29 || $[101] !== t30 || $[102] !== toolbarSlot) {
    t31 = <div className="bg-ds-surface-primary shadow-ds-float-deep flex flex-col gap-3 rounded-lg p-3">{toolbarSlot}{selectionIndicatorSlot}{t18}{t28}{t29}{commentsBadgeSlot}{t30}</div>;
    $[96] = commentsBadgeSlot;
    $[97] = selectionIndicatorSlot;
    $[98] = t18;
    $[99] = t28;
    $[100] = t29;
    $[101] = t30;
    $[102] = toolbarSlot;
    $[103] = t31;
  } else {
    t31 = $[103];
  }
  let t32;
  if ($[104] !== t11 || $[105] !== t12 || $[106] !== t13 || $[107] !== t14 || $[108] !== t15 || $[109] !== t16 || $[110] !== t17 || $[111] !== t31) {
    t32 = <div {...t11} ref={t12} data-api-node-id={t13} onMouseEnter={t14} onMouseLeave={t15} className={t16}>{t17}{t31}</div>;
    $[104] = t11;
    $[105] = t12;
    $[106] = t13;
    $[107] = t14;
    $[108] = t15;
    $[109] = t16;
    $[110] = t17;
    $[111] = t31;
    $[112] = t32;
  } else {
    t32 = $[112];
  }
  return t32;
}
export default TriggerNodeShell;